import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MY_DAY_PATH } from '../my-day/my-day-route';
import { SETTINGS_PATH } from '../settings/settings-route';
import { GLOBAL_SOL_PATH } from '../sol/sol-routes';
import { sessionOwnerUserId, useSession } from '../auth/session';
import { useProjects } from '../../data/queries/use-projects';
import { useRecoverableCaptureDraft } from '../captures/use-capture-draft';
import { CaptureRecoveryCard } from '../captures/CaptureRecoveryCard';
import { CreateProjectSheet } from './CreateProjectSheet';
import { formatInstant } from '../../lib/timezone';
import { PROJECT_STATUS_LABEL } from './status';
import type { ProjectRow, ProjectStatus } from '../../data/database.types';
import './projects.css';

/**
 * Projects — the real list (design export S02) and the empty state (S03).
 *
 * Ongoing first and always expanded; Finished and Closed as separate collapsed
 * groups with counts, shown only when they contain something (PT §4).
 *
 * Rows are REAL navigation as of S-03: tapping one opens that Project's
 * Project Home. They were deliberately inert until the destination existed.
 *
 * NOT here, by scope:
 *   * Changing status — that lives on Project Home (S-03), not in this list.
 *   * Search Projects — the export's populated screen shows a search field, but
 *     it is outside M1's locked scope and is deliberately not built.
 *
 * AS OF M4 / S-04-01 THIS SCREEN CARRIES THE RECOVERY CARD. It is the default
 * entry (PT §3), so it is where Brandon lands after a cold launch — which makes
 * it the surface where an unfinished Capture has to announce itself. The card
 * OFFERS; it never navigates on its own (D-019 J-3), and it is rendered above
 * the Project list because a Capture he has already spoken outranks the list of
 * jobs he might pick next.
 */

export function ProjectsScreen() {
  const session = useSession();
  const { signOut, profile } = session;
  const navigate = useNavigate();
  const { status, projects, error, create } = useProjects();
  const { state: draftState, discard: discardDraft } = useRecoverableCaptureDraft(
    sessionOwnerUserId(session),
  );
  const [sheetOpen, setSheetOpen] = useState(false);
  const [showFinished, setShowFinished] = useState(false);
  const [showClosed, setShowClosed] = useState(false);

  const grouped = useMemo(
    () => ({
      ongoing: projects.filter((project) => project.status === 'ongoing'),
      finished: projects.filter((project) => project.status === 'finished'),
      closed: projects.filter((project) => project.status === 'closed'),
    }),
    [projects],
  );

  const isEmpty = status === 'ready' && projects.length === 0;

  async function handleCreate(name: string) {
    // Throws on failure, which the sheet catches and displays. The sheet closes
    // only on success, so nothing claims a Project exists before the server
    // says it does (PT §20.1).
    await create(name);
    setSheetOpen(false);
  }

  return (
    <main className="cl-screen projects-screen">
      <header className="projects-header">
        <h1 className="cl-screen-title">Projects</h1>
        <div className="projects-header-actions">
          {!isEmpty && status !== 'loading' ? (
            <button
              type="button"
              className="projects-add"
              onClick={() => setSheetOpen(true)}
              aria-label="Create Project"
            >
              +
            </button>
          ) : null}
          {/* GLOBAL SOL (M11 / D-041). The same Sol, entered with no Project on
              screen — so a question that is not about one job ("how many
              Projects do I have?", "what did we decide about Lakeside?") has
              a place to be asked. Quiet, beside the other top-level actions;
              no redesign of the screen. */}
          {/* MY DAY (M12 / S12-01). Beside the other top-level actions, in the
              existing header style — **no tab bar and no navigation redesign**,
              which this slice deliberately does not attempt. */}
          <Link className="cl-button cl-button--quiet" to={MY_DAY_PATH}>
            My Day
          </Link>
          <Link className="cl-button cl-button--quiet" to={GLOBAL_SOL_PATH}>
            Ask Sol
          </Link>
          {/* SETTINGS SITS BESIDE SIGN OUT because that is already the
              account area of this screen — the lowest-friction placement, and
              it does not turn Settings into a primary destination competing
              with Projects (M10 / S10-03A). */}
          <Link className="cl-button cl-button--quiet" to={SETTINGS_PATH}>
            Settings
          </Link>
          <button
            type="button"
            className="cl-button cl-button--quiet"
            onClick={() => void signOut()}
          >
            Sign out
          </button>
        </div>
      </header>

      {/* Recovery is offered before anything else on the screen and NEVER
          entered automatically. Continue carries an explicit intent flag into
          the Capture route — the Capture screen restores only when asked, so
          an ordinary visit to that route can never silently adopt old text. */}
      {draftState.status === 'found' ? (
        <CaptureRecoveryCard
          draft={draftState.draft}
          nowMs={draftState.readAtMs}
          audio={draftState.audio}
          onContinue={() => {
            void navigate(`/projects/${draftState.draft.projectId}/capture`, {
              state: { recoverDraft: true },
            });
          }}
          onDiscard={() => void discardDraft()}
        />
      ) : null}

      {status === 'loading' ? (
        <p className="projects-loading" role="status">
          Loading your Projects…
        </p>
      ) : null}

      {error ? (
        <p className="cl-error" role="alert">
          {error}
        </p>
      ) : null}

      {isEmpty ? (
        <section className="projects-empty">
          <div className="projects-empty-mark" aria-hidden="true">
            +
          </div>
          <h2 className="projects-empty-title">No projects yet</h2>
          <p className="projects-empty-body">
            A Project is the container for every Capture. Create the job you&rsquo;re standing on
            right now.
          </p>

          <button
            className="cl-button projects-empty-action"
            type="button"
            onClick={() => setSheetOpen(true)}
          >
            Create Project
          </button>
        </section>
      ) : null}

      {status === 'ready' && projects.length > 0 ? (
        <section className="projects-list">
          <h2 className="projects-group-heading">ONGOING · {grouped.ongoing.length}</h2>
          {grouped.ongoing.length === 0 ? (
            <p className="projects-group-empty">
              No ongoing Projects. Finished and Closed ones are below.
            </p>
          ) : null}
          {grouped.ongoing.map((project) => (
            <OngoingCard key={project.id} project={project} timeZone={profile?.timezone ?? null} />
          ))}

          <CollapsedGroup
            label="FINISHED"
            status="finished"
            projects={grouped.finished}
            open={showFinished}
            onToggle={() => setShowFinished((value) => !value)}
          />
          <CollapsedGroup
            label="CLOSED"
            status="closed"
            projects={grouped.closed}
            open={showClosed}
            onToggle={() => setShowClosed((value) => !value)}
          />
        </section>
      ) : null}

      {sheetOpen ? (
        <CreateProjectSheet onCancel={() => setSheetOpen(false)} onCreate={handleCreate} />
      ) : null}
    </main>
  );
}

function OngoingCard({ project, timeZone }: { project: ProjectRow; timeZone: string | null }) {
  // The design's card carries a meta line. There are no Captures yet, so the
  // only truthful thing to put there is when the Project was created — and it
  // is rendered ONLY when the canonical Crewlink timezone is known. Falling
  // back to the device zone would violate INV-T17, and showing nothing is
  // better than showing a time in a zone we guessed.
  const meta = timeZone
    ? `Created ${formatInstant(project.created_at, timeZone, { dateStyle: 'medium' })}`
    : null;

  return (
    <Link className="project-card project-card--ongoing" to={`/projects/${project.id}`}>
      <div className="project-card-top">
        <h3 className="project-card-name">{project.name}</h3>
        <span className="status-chip status-chip--ongoing">{PROJECT_STATUS_LABEL.ongoing}</span>
      </div>
      {meta ? <p className="project-card-meta">{meta}</p> : null}
    </Link>
  );
}

function CollapsedGroup({
  label,
  status,
  projects,
  open,
  onToggle,
}: {
  label: string;
  status: Extract<ProjectStatus, 'finished' | 'closed'>;
  projects: ProjectRow[];
  open: boolean;
  onToggle: () => void;
}) {
  // Shown only when the group holds something — an empty "FINISHED · 0" row is
  // noise on a screen whose whole job is to show the work in front of Brandon.
  if (projects.length === 0) return null;

  return (
    <>
      <button
        type="button"
        className="projects-group-toggle"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className="projects-group-heading">
          {label} · {projects.length}
        </span>
        <span className="projects-group-caret" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
      </button>

      {open
        ? projects.map((project) => (
            <Link
              key={project.id}
              className={`project-card project-card--${status}`}
              to={`/projects/${project.id}`}
            >
              <h3 className="project-card-name project-card-name--muted">{project.name}</h3>
              <span className={`status-chip status-chip--${status}`}>
                {PROJECT_STATUS_LABEL[status]}
              </span>
            </Link>
          ))
        : null}
    </>
  );
}
